import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Layout from 'shared/components/Layout';
import ProtectedRoute from 'shared/components/ProtectedRoute';
import SessionListener from 'shared/components/SessionListener';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import ToastProvider from 'shared/components/ToastProvider';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { isTokenExpired } from 'lib/auth/authUtils';

const LoginPage = lazy(() => import('features/auth/LoginPage'));
const ExplorePage = lazy(() => import('features/explore/ExplorePage'));
const CheckoutPage = lazy(() => import('features/orders/CheckoutPage'));
const OrderPage = lazy(() => import('features/orders/OrderPage'));
const SettingsPage = lazy(() => import('features/settings/SettingsPage'));
const DataDeliveryPage = lazy(() => import('features/delivery/DataDeliveryPage'));
const ProductDetailPage = lazy(() => import('features/products/ProductDetailPage'));
const ProductCatalog = lazy(() => import('features/products/ProductCatalog'));
const DocumentationRoutes = lazy(() => import('features/documentation/DocumentationRoutes'));

const PageLoader = () => <LoadingSpinner fullPage />;

/**
 * Keeps ExplorePage mounted (preserving the Leaflet map and all state)
 * even when navigating to other routes. Hidden via CSS when inactive.
 */
const PersistentExplorePage = () => {
  const location = useLocation();
  const isActive = location.pathname === '/explore';
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (isActive && !hasLoaded) setHasLoaded(true);
  }, [isActive, hasLoaded]);

  // Don't mount until the user visits /explore for the first time
  if (!hasLoaded || isTokenExpired()) return null;

  return (
    <div
      style={{
        visibility: isActive ? 'visible' : 'hidden',
        pointerEvents: isActive ? 'auto' : 'none',
        position: isActive ? 'relative' : 'fixed',
        zIndex: isActive ? undefined : -1,
        height: '100%',
      }}
    >
      <ExplorePage />
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <SessionListener />
      <ToastProvider />

      <Layout>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <PersistentExplorePage />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<LoginPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/orders" element={<OrderPage />} />
                <Route path="/settings/*" element={<SettingsPage />} />
                <Route path="/delivery/:orderId" element={<DataDeliveryPage />} />
                <Route path="/products" element={<ProductCatalog />} />
                <Route path="/products/:productId" element={<ProductDetailPage />} />
                <Route path="/documentation/*" element={<DocumentationRoutes />} />
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Layout>
    </Router>
  );
};

export default App;
